
import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    userEmail: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      const res = await fetch('https://readdy.ai/api/form/d67bjq6a728k8ctu1q7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', userEmail: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl p-7 shadow-sm"
    >
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900">Mesaj Gönderin</h3>
        <p className="text-sm text-gray-500 mt-1">Formu doldurun, en kısa sürede dönüş yapalım.</p>
      </div>

      <form
        id="contact-page-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="cp-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Ad Soyad *
            </label>
            <input
              type="text"
              id="cp-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Adınız ve soyadınız"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none"
            />
          </div>
          <div>
            <label htmlFor="cp-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
              Telefon *
            </label>
            <input
              type="tel"
              id="cp-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="0555 123 45 67"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="cp-userEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
              E-posta *
            </label>
            <input
              type="email"
              id="cp-userEmail"
              name="userEmail"
              value={formData.userEmail}
              onChange={handleChange}
              required
              placeholder="ornek@email.com"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none"
            />
          </div>
          <div>
            <label htmlFor="cp-subject" className="block text-sm font-medium text-gray-700 mb-1.5">
              Konu *
            </label>
            <select
              id="cp-subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none bg-white cursor-pointer"
            >
              <option value="">Seçiniz</option>
              <option value="Teklif Talebi">Teklif Talebi</option>
              <option value="Yangın Algılama Sistemi">Yangın Algılama Sistemi</option>
              <option value="Duman Sensörü">Duman Sensörü</option>
              <option value="Alarm Sistemi">Alarm Sistemi</option>
              <option value="Bakım & Servis">Bakım &amp; Servis</option>
              <option value="Teknik Destek">Teknik Destek</option>
              <option value="Diğer">Diğer</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="cp-message" className="block text-sm font-medium text-gray-700 mb-1.5">
            Mesajınız *
          </label>
          <textarea
            id="cp-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            maxLength={500}
            rows={5}
            placeholder="Mesajınızı buraya yazın..."
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none resize-none"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-red-600 text-white py-3.5 rounded-lg font-semibold hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-send-plane-line"></i> Mesaj Gönder
            </span>
          )}
        </button>

        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm"
          >
            <i className="ri-check-line mr-2"></i>
            Mesajınız başarıyla gönderildi. En kısa sürede dönüş yapacağız.
          </motion.div>
        )}
        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm"
          >
            <i className="ri-error-warning-line mr-2"></i>
            Bir hata oluştu. Lütfen tekrar deneyin.
          </motion.div>
        )}
      </form>
    </motion.div>
  );
}